import { Badge } from "@comps/ui/badge";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@comps/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@comps/ui/popover";
import Field from "@dto/fields";
import { Record } from "@dto/records";
import { TW_RecordSelectPopoverContent, TW_FilterEditorRecordSelect, TW_SelectionRecordBadge, TW_SelectRecordCommandItem, TW_SelectRecordCommandItemRecordText, TW_SelectRecordCommandItemSelected, TW_SelectRecordScrollArea, TW_FilterEditorRecordSelectTriggerDisabled, TW_FilterEditorRecordSelectTrigger, TW_SelectionRecordBadgeMore, TW_CommonCommandInput, getBadgeStyle, TW_SelectRecordCommandList, getWsButtonStyle, useHover } from "@utils/twClasses";
import { valueToString } from "@utils/types";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useModelStore } from "src/store";


type RecordSelectProps = {
    pathKey: string | null;
    field: Field | null;
    allRecords: Record[];
    selectedRecords: Record[];
    disabled: boolean;
    onSelectionChange: (records: Record[]) => void;
}

export function RecordSelect({ pathKey, field, allRecords, selectedRecords, disabled, onSelectionChange }: RecordSelectProps) {

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const wsColor = useModelStore(state => state.wsColor);
    const selectedView = useModelStore(state => state.selectedView);
    const { t } = useTranslation();
    const fieldByKeys = useModelStore(state => state.fieldByKeys);
    const format = selectedView?.formats?.get(pathKey ?? "") ?? field?.format;
    const badgeStyle = useMemo(() => {
        return getBadgeStyle(wsColor);
    }, [wsColor]);

    function getRecordDisplay(record: Record) {
        const fieldValue = Array.from(record.fields.values())[0];
        const valueStr = field ? valueToString(fieldValue, field, field.aggregation, format, t, "", fieldByKeys) : fieldValue;
        return valueStr;
    }

    const records = selectedRecords.filter(record => record.fields);
    const firstRecord = (records.length > 0) ? records[0] : null;
    const firstRecordValueStr = firstRecord ? getRecordDisplay(firstRecord) : null;

    const nextRecords = selectedRecords.filter(record => record._key !== firstRecord?._key);
    const nextTooltip = nextRecords.map(record => getRecordDisplay(record)).join(", ");
    return (
        <Popover open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <PopoverTrigger disabled={disabled} className={(disabled ? " " + TW_FilterEditorRecordSelectTriggerDisabled : TW_FilterEditorRecordSelectTrigger)}>
                <div className={TW_FilterEditorRecordSelect}>
                    {firstRecordValueStr && <Badge key={firstRecord?._key} className={TW_SelectionRecordBadge} style={badgeStyle}>{firstRecordValueStr}</Badge>}
                    {nextRecords.length > 0 && <Badge key={nextRecords[0]?._key} className={TW_SelectionRecordBadgeMore} tooltip={nextTooltip} style={badgeStyle}>{"+" + nextRecords.length}</Badge>}
                </div>
            </PopoverTrigger>
            <PopoverContent className={TW_RecordSelectPopoverContent}>
                <RecordSelectForm pathKey={pathKey} field={field} allRecords={allRecords} selectedRecords={selectedRecords} onSelectionChange={onSelectionChange} />
            </PopoverContent>
        </Popover>
    )
}


interface RecordSelectFormProps {
    pathKey: string | null;
    field: Field | null;
    allRecords: Record[];
    selectedRecords: Record[];
    onSelectionChange: (records: Record[]) => void;
}

function RecordSelectForm({ pathKey, field, allRecords, selectedRecords, onSelectionChange }: RecordSelectFormProps) {

    const { t } = useTranslation();
    const selectedRecordKeys = selectedRecords.map(record => record._key);
    const wsColor = useModelStore(state => state.wsColor);
    const wsLightColor = useModelStore(state => state.wsLightColor);
    const wsDarkColor = useModelStore(state => state.wsDarkColor);

    
    const onRecordSelect = (recordKey: string) => {
        if (selectedRecordKeys.includes(recordKey)) {
            onSelectionChange(selectedRecords.filter(record => record._key !== recordKey));
        } else {
            onSelectionChange([...selectedRecords, allRecords.find(record => record._key === recordKey)!]);
        }
    }
    
    function onCommandFilter(value: string, search: string, keywords?: string[]) {
        if (value.trim().toLowerCase().startsWith(search.trim().toLowerCase())) {
            return 1;
        } else if (value.trim().toLowerCase().includes(search.trim().toLowerCase())) {
            return 0.5;
        } else {
            return 0;
        }
    }

    const scrollAreaStyle = {
        scrollbarColor: wsColor + " " + wsLightColor,
        scrollbarThumbColor: wsDarkColor,
        scrollbarTrackColor: wsLightColor,
    }

    if (allRecords.length === 0) {
        return (
            <div>
                <h1>{t('data.table.noRecords')}</h1>
            </div>
        )
    } else {
        return (

            <Command filter={onCommandFilter}>
                <CommandInput placeholder={t("data.record.search.placeholder")} className={TW_CommonCommandInput} autoFocus={true}  />
                <div className={TW_SelectRecordScrollArea}>
                    <CommandList className={TW_SelectRecordCommandList}  style={scrollAreaStyle}>
                        <CommandEmpty>{t("data.table.noRecords")}</CommandEmpty>
                        <CommandGroup>
                            {allRecords.map((record) => {
                                return (
                                    <RecordSelectItem key={"recordSelectItem_" + record._key} pathKey={pathKey} field={field} record={record} selectedRecordKeys={selectedRecordKeys} onRecordSelect={onRecordSelect} />
                                )
                            })}
                        </CommandGroup>
                    </CommandList>
                </div>
            </Command>

        )
    }
}

function RecordSelectItem({ pathKey, field, record, selectedRecordKeys, onRecordSelect }: { pathKey: string | null, field: Field | null, record: Record, selectedRecordKeys: string[], onRecordSelect: (recordKey: string) => void }) {
    const { t } = useTranslation();
    const fieldByKeys = useModelStore(state => state.fieldByKeys);
    const wsLightColor = useModelStore(state => state.wsLightColor);
    const wsDarkColor = useModelStore(state => state.wsDarkColor);
    const useButtonHover = useHover(getWsButtonStyle(), getWsButtonStyle(wsLightColor, wsDarkColor));
    const fieldValue = Array.from(record.fields.values())[0];
    const selectedView = useModelStore(state => state.selectedView);
    const format = selectedView?.formats?.get(pathKey ?? "") ?? field?.format;
    const valueStr = field ? valueToString(fieldValue, field, field.aggregation, format, t, "", fieldByKeys) : fieldValue;


    const selectedRecordStyle = {
        backgroundColor: wsLightColor,
        borderColor: wsDarkColor,
    }

    return (
        <CommandItem key={record._key} className={`${selectedRecordKeys.includes(record._key) ? TW_SelectRecordCommandItemSelected : TW_SelectRecordCommandItem}`} style={selectedRecordKeys.includes(record._key) ? selectedRecordStyle : useButtonHover.style} onSelect={() => onRecordSelect(record._key)} onMouseEnter={useButtonHover.onMouseEnter} onMouseLeave={useButtonHover.onMouseLeave}>
            <span className={TW_SelectRecordCommandItemRecordText}>{valueStr}</span>
        </CommandItem>
    )
}

